import {List} from "immutable";
import React, {useEffect, useImperativeHandle, useRef, useState} from "react";
import {Menu, Message} from "semantic-ui-react";
import CodeEditor from "../../../core/components/CodeEditor/CodeEditor";
import CollaborationInfoModal from "../../../standalone/components/CollaborationInfoModal";
import {isStandalone} from "../../../standalone/util";
import {useElementsTree} from "../../hooks/useElementsTree";
import {Board, BoardId} from "../../model/Board";
import {Node, NodeType} from "../../model/Graph";
import {StickyTemplate} from "../../model/StickyTemplate";
import {ActiveGraphElement} from "../../reducers/applyActiveGraphElementActions";
import {parse} from "../../service/json/json-comment-wrapper";
import {isConnected} from "../BoardElementDetails";
import BoardHistory from "../BoardHistory";
import ElementConnections from "./ElementConnections";
import MetadataCodyWizard from "./MetadataCodyWizard";

export interface BoardElementMetadataRef {
    retrievePayload: () => string;
}

interface OwnProps {
    focusEditor: boolean;
    boardId: BoardId;
    board?: Board;
    activeNodeSources: List<Node>;
    activeNodeTargets: List<Node>;
    activeGraphElement: ActiveGraphElement;
    similarElements: List<string>;
    boardStickyTemplate: StickyTemplate;
    onTriggerCody: () => void;
    onUpdateMetadata: (value: string, force?: boolean) => void;
    onShowRunCody: (showRunCody: boolean) => void;
    onClose?: () => void;
    t: any; // Workaround for withNamespaces HOC not working with forwardRef
}

export type ActiveTab = 'wizard' | 'metadata' | 'activity';

type BoardElementMetadataProps = OwnProps;

const errorRegistry: Record<string, string> = {};

const BoardElementMetadata = (props: BoardElementMetadataProps, ref: React.Ref<BoardElementMetadataRef>) => {
    const elementsTree = useElementsTree(props.boardId);
    const codeEditorRef = useRef<CodeEditor|null>(null);
    const [activeTab, setActiveTab] = useState<ActiveTab>(props.focusEditor ? 'metadata' : 'wizard');
    const [infoModalOpen, setInfoModalOpen] = useState(false);

    const isNodeWithMetadata = props.activeGraphElement.type !== NodeType.textCard
        && props.activeGraphElement.type !== NodeType.edge;

    const handleMetadataUpdate = (metadata: string, force?: boolean): void => {
        if(props.boardStickyTemplate.templateType !== "json") {
            props.onUpdateMetadata(metadata, force);
        }

        try {
            parse(metadata);
            props.onUpdateMetadata(metadata, force);
            delete errorRegistry[props.activeGraphElement.id];
        } catch (e) {
            errorRegistry[props.activeGraphElement.id] = metadata;
        }
    }

    useImperativeHandle<BoardElementMetadataRef, BoardElementMetadataRef>(ref, () => ({
        retrievePayload: (): string => codeEditorRef.current!.retrievePayload(),
    }));

    useEffect(() => {
        if(!props.board || !props.board.codySuggestEnabled) {
            setActiveTab('metadata');
        }
    }, [props.boardId, (props.board && props.board.codySuggestEnabled)]);

    useEffect(() => {
        if (!codeEditorRef.current) {
            return;
        }

        const disposable = codeEditorRef.current!.addKeyDownHandler((e: KeyboardEvent) => {
            if (e.ctrlKey && (e.code === 'KeyS' || e.code === 'KeyG')) {
                e.preventDefault();

                handleMetadataUpdate(codeEditorRef.current!.retrievePayload(), true);
                props.onTriggerCody();
            }

            if(e.code === 'Escape' && props.onClose) {
                handleMetadataUpdate(codeEditorRef.current!.retrievePayload());
                e.preventDefault();
                e.stopPropagation();
                props.onClose();
            }
        });

        return () => {
            disposable.dispose();
        }
    }, [props.activeGraphElement.id]);

    useEffect(() => {
        if(activeTab !== 'metadata') {
            return;
        }

        if (!isNodeWithMetadata || !codeEditorRef.current) {
            return;
        }

        if(!props.activeGraphElement.metadata && isConnected(props.activeGraphElement) && props.similarElements.count() > 0) {
            const firstSimilarElement = elementsTree.getElement(props.similarElements.first(''));

            if(firstSimilarElement && firstSimilarElement.getMetadata()) {
                handleMetadataUpdate(firstSimilarElement.getMetadata() as string, true);
            }
        }

        codeEditorRef.current!.initializeModel({
            fileId: `graph_element_${props.activeGraphElement.id}.json`,
            language: props.boardStickyTemplate.templateType,
            value: errorRegistry[props.activeGraphElement.id] || props.activeGraphElement.metadata || props.boardStickyTemplate.template || '{\n  \n}\n',
            schema: props.boardStickyTemplate.templateSchema || undefined,
            onChange: e => {
                if (codeEditorRef.current) {
                    handleMetadataUpdate(codeEditorRef.current.retrievePayload());
                }
            },
        });
    }, [props.activeGraphElement.id, props.activeGraphElement.metadata, props.similarElements, activeTab])

    useEffect(() => {
        if(props.focusEditor && activeTab !== 'metadata') {
            changeActiveTab('metadata');
        }
        if(codeEditorRef.current && props.focusEditor) {
            codeEditorRef.current!.focus();
        }
    }, [props.focusEditor, activeTab]);

    const changeActiveTab = (changedTab: ActiveTab) => {
        if (changedTab === 'activity' && isStandalone()) {
            setInfoModalOpen(true);
            return;
        }
        setActiveTab(changedTab);
        props.onShowRunCody(changedTab === "wizard");
    }

    const invalidJson = !!errorRegistry[props.activeGraphElement.id];

    return (<>
        {(props.activeNodeSources.size > 0 || props.activeNodeTargets.size > 0) && (
            <ElementConnections
                boardId={props.boardId}
                sources={props.activeNodeSources}
                targets={props.activeNodeTargets}
            />
        )}
        <Menu pointing={true} secondary={true} color="blue">
            <Menu.Item name="Cody" active={activeTab === 'wizard'} onClick={() => changeActiveTab('wizard')} />
            <Menu.Item name="Metadata" active={activeTab === 'metadata'} onClick={() => changeActiveTab('metadata')} />
            <Menu.Item name="Activity" active={activeTab === 'activity'} onClick={() => changeActiveTab('activity')} />
        </Menu>
        {isNodeWithMetadata && activeTab === 'wizard' && (
          <div style={{height: 'auto', overflowY: 'auto', flexGrow: 1}}>
            <MetadataCodyWizard boardId={props.boardId} activeGraphElement={props.activeGraphElement} onUpdateMetadata={props.onUpdateMetadata} />
          </div>
        )}
        {isNodeWithMetadata && activeTab === 'metadata' && (<>
            {invalidJson &&
              <Message error={true} size="small" content={props.t('insp.cody_wizard.schema_editor.invalid_json') as string}
                       icon="warning" style={{display: 'flex'}}/>}
            <CodeEditor
                ref={codeEditorRef}
                options={{
                    fontSize: 12,
                    folding: false,
                    glyphMargin: false,
                    lineNumbers: 'off',
                    lineDecorationsWidth: 3,
                    minimap: {
                        enabled: false
                    },
                    formatOnPaste: true,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    scrollbar: {
                        alwaysConsumeMouseWheel: false
                    },
                    readOnly: props.activeGraphElement.locked
                }}
                style={{ height: '500px' }}

            />
        </>)}
        {activeTab === 'activity' && <BoardHistory elementId={props.activeGraphElement.id} />}
        <CollaborationInfoModal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} headerKey={'header_history'} />
    </>);
}

export default React.forwardRef(BoardElementMetadata);
